import type { GoalSnapshotResponse, SnapshotType } from "@repo/shared/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as goalSnapshotsApi from "@/lib/api/flow-instances/goal-snapshots";
import * as publicGoalSnapshotsApi from "@/lib/api/public-flow/goal-snapshots";
import * as publicInstanceSnapshotsApi from "@/lib/api/public-flow/instance-snapshots";
import { usePublicSession } from "@/lib/context/public-session-context";

// ─── Query keys ──────────────────────────────────────────────────────────────

export const goalSnapshotQueryKeys = {
  portfolio: (goalId: string | null) =>
    ["flowInstances", "goals", goalId, "snapshot", "portfolio"] as const,
  xray: (goalId: string | null) => ["flowInstances", "goals", goalId, "snapshot", "xray"] as const,
  assetAllocation: (goalId: string | null) =>
    ["flowInstances", "goals", goalId, "snapshot", "assetAllocation"] as const,
  historicalReturn: (goalId: string | null) =>
    ["flowInstances", "goals", goalId, "snapshot", "historicalReturn"] as const,
  forecast: (goalId: string | null) =>
    ["flowInstances", "goals", goalId, "snapshot", "forecast"] as const,
  cost: (goalId: string | null) => ["flowInstances", "goals", goalId, "snapshot", "cost"] as const,
  expectedValue: (goalId: string | null) =>
    ["flowInstances", "goals", goalId, "snapshot", "expectedValue"] as const,
  sustainability: (goalId: string | null) =>
    ["flowInstances", "goals", goalId, "snapshot", "sustainability"] as const,
  /** QL-315 — goal-scoped asset-class forecast. */
  assetClassForecast: (goalId: string | null) =>
    ["flowInstances", "goals", goalId, "snapshot", "assetClassForecast"] as const,
  /** QL-416 — the goal-keyed shape exists for Record exhaustiveness only; the
   *  snapshot is portfolio-keyed and read via usePortfolioSnapshot. */
  costBenefit: (goalId: string | null) =>
    ["flowInstances", "goals", goalId, "snapshot", "costBenefit"] as const,
  /** QL-314 — volatility chart (reads asset_class_forecast snapshot server-side). */
  volatilityChart: (goalId: string | null) =>
    ["flowInstances", "goals", goalId, "snapshot", "volatilityChart"] as const,
  /** QL-313 — equity share chart (reads asset_class_forecast snapshot server-side). */
  equityShareChart: (goalId: string | null) =>
    ["flowInstances", "goals", goalId, "snapshot", "equityShareChart"] as const,
  /** QL-375 — the mandate check (goal scope; the portfolio scope appends the portfolio id). */
  mandate: (goalId: string | null) =>
    ["flowInstances", "goals", goalId, "snapshot", "mandate"] as const,
  /** QL-375 group 8 — existing vs new; under the mandate key so every mandate invalidation reaches it. */
  mandateComparison: (goalId: string | null) =>
    ["flowInstances", "goals", goalId, "snapshot", "mandate", "comparison"] as const,
  /** Matches all snapshot queries for a goal — for bulk invalidation */
  allForGoal: (goalId: string) => ["flowInstances", "goals", goalId, "snapshot"] as const,
  instanceCost: (instanceId: string | null) =>
    ["flowInstances", instanceId, "snapshot", "instanceCost"] as const,
  instanceExpectedValue: (instanceId: string | null) =>
    ["flowInstances", instanceId, "snapshot", "instanceExpectedValue"] as const,
  /** QL-315 — instance-wide asset-class forecast (the global-page placement). */
  instanceAssetClassForecast: (instanceId: string | null) =>
    ["flowInstances", instanceId, "snapshot", "instanceAssetClassForecast"] as const,
};

// ─── Query hooks ─────────────────────────────────────────────────────────────

function useSnapshot(
  goalId: string | null,
  queryKey: readonly unknown[],
  queryFn: (id: string) => Promise<GoalSnapshotResponse>,
) {
  const { data, error, isLoading } = useQuery({
    queryKey,
    queryFn: () => queryFn(goalId!),
    enabled: !!goalId,
  });
  return { data: data?.data ?? null, generatedAt: data?.generatedAt ?? null, error, isLoading };
}

export function useGoalPortfolioSnapshot(goalId: string | null) {
  return useSnapshot(
    goalId,
    goalSnapshotQueryKeys.portfolio(goalId),
    goalSnapshotsApi.getGoalPortfolioSnapshot,
  );
}

export function useGoalXraySnapshot(goalId: string | null) {
  return useSnapshot(
    goalId,
    goalSnapshotQueryKeys.xray(goalId),
    goalSnapshotsApi.getGoalXraySnapshot,
  );
}

export function useGoalAssetAllocationSnapshot(goalId: string | null) {
  return useSnapshot(
    goalId,
    goalSnapshotQueryKeys.assetAllocation(goalId),
    goalSnapshotsApi.getGoalAssetAllocationSnapshot,
  );
}

export function useGoalHistoricalReturnSnapshot(goalId: string | null) {
  return useSnapshot(
    goalId,
    goalSnapshotQueryKeys.historicalReturn(goalId),
    goalSnapshotsApi.getGoalHistoricalReturnSnapshot,
  );
}

export function useGoalForecastSnapshot(goalId: string | null) {
  return useSnapshot(
    goalId,
    goalSnapshotQueryKeys.forecast(goalId),
    goalSnapshotsApi.getGoalForecastSnapshot,
  );
}

export function useGoalCostSnapshot(goalId: string | null) {
  return useSnapshot(
    goalId,
    goalSnapshotQueryKeys.cost(goalId),
    goalSnapshotsApi.getGoalCostSnapshot,
  );
}

export function useGoalExpectedValueSnapshot(goalId: string | null) {
  return useSnapshot(
    goalId,
    goalSnapshotQueryKeys.expectedValue(goalId),
    goalSnapshotsApi.getGoalExpectedValueSnapshot,
  );
}

/**
 * QL-315 — the goal-scoped asset-class forecast. Public-capable, unlike the
 * goal hooks above: this component renders on the client-facing public flow as
 * well as the advisor wizard, so it needs the same advisor/public split the
 * instance-level hooks below use.
 */
export function useGoalAssetClassForecastSnapshot(goalId: string | null) {
  const session = usePublicSession();
  const queryKey = session.isPublic
    ? (["publicFlow", "goals", goalId, "snapshot", "assetClassForecast"] as const)
    : goalSnapshotQueryKeys.assetClassForecast(goalId);
  return useSnapshot(
    goalId,
    queryKey,
    session.isPublic
      ? (id: string) =>
          publicGoalSnapshotsApi.getPublicGoalAssetClassForecastSnapshot(session.sessionToken, id)
      : goalSnapshotsApi.getGoalAssetClassForecastSnapshot,
  );
}

// Instance-level snapshot hooks — public-capable (audit §7). Inside
// `PublicSessionProvider` they read the public instance routes; otherwise the
// advisor route. The advisor query key is unchanged (so advisor invalidation keeps
// working); public uses a separate `publicFlow`-prefixed key.
export function useInstanceCostSnapshot(instanceId: string | null) {
  const session = usePublicSession();
  const queryKey = session.isPublic
    ? (["publicFlow", "instance", instanceId, "snapshot", "instanceCost"] as const)
    : goalSnapshotQueryKeys.instanceCost(instanceId);
  return useSnapshot(
    instanceId,
    queryKey,
    session.isPublic
      ? () => publicInstanceSnapshotsApi.getPublicInstanceCostSnapshot(session.sessionToken)
      : goalSnapshotsApi.getInstanceCostSnapshot,
  );
}

export function useInstanceExpectedValueSnapshot(instanceId: string | null) {
  const session = usePublicSession();
  const queryKey = session.isPublic
    ? (["publicFlow", "instance", instanceId, "snapshot", "instanceExpectedValue"] as const)
    : goalSnapshotQueryKeys.instanceExpectedValue(instanceId);
  return useSnapshot(
    instanceId,
    queryKey,
    session.isPublic
      ? () =>
          publicInstanceSnapshotsApi.getPublicInstanceExpectedValueSnapshot(session.sessionToken)
      : goalSnapshotsApi.getInstanceExpectedValueSnapshot,
  );
}

/** QL-315 — the instance-wide asset-class forecast (global-page placement). */
export function useInstanceAssetClassForecastSnapshot(instanceId: string | null) {
  const session = usePublicSession();
  const queryKey = session.isPublic
    ? (["publicFlow", "instance", instanceId, "snapshot", "instanceAssetClassForecast"] as const)
    : goalSnapshotQueryKeys.instanceAssetClassForecast(instanceId);
  return useSnapshot(
    instanceId,
    queryKey,
    session.isPublic
      ? () =>
          publicInstanceSnapshotsApi.getPublicInstanceAssetClassForecastSnapshot(
            session.sessionToken,
          )
      : goalSnapshotsApi.getInstanceAssetClassForecastSnapshot,
  );
}

// ─── Invalidation helper ─────────────────────────────────────────────────────

const SNAPSHOT_TYPE_TO_KEY: Record<SnapshotType, (goalId: string | null) => readonly unknown[]> = {
  portfolio: goalSnapshotQueryKeys.portfolio,
  // QL-707 — portfolio-keyed advised rows; never emitted in refreshHints (the
  // builder reads them through the portfolio GET). Entry exists only for
  // Record exhaustiveness, like clientPreferences.
  advisedPortfolio: () => ["flowInstances", "snapshot", "advisedPortfolio"] as const,
  xray: goalSnapshotQueryKeys.xray,
  assetAllocation: goalSnapshotQueryKeys.assetAllocation,
  historicalReturn: goalSnapshotQueryKeys.historicalReturn,
  forecast: goalSnapshotQueryKeys.forecast,
  cost: goalSnapshotQueryKeys.cost,
  expectedValue: goalSnapshotQueryKeys.expectedValue,
  sustainability: goalSnapshotQueryKeys.sustainability,
  // Instance-global (QP-224); the server never emits it in refreshHints — the
  // entry exists only for Record exhaustiveness.
  clientPreferences: () => ["flowInstances", "snapshot", "clientPreferences"] as const,
  // QL-315 — the goal row. Its instance-wide sibling is a key-shape away from
  // this subtree and is handled by invalidateInstanceAssetClassForecast below.
  assetClassForecast: goalSnapshotQueryKeys.assetClassForecast,
  // QL-375 — the goal-scoped mandate check.
  mandate: goalSnapshotQueryKeys.mandate,
  costBenefit: goalSnapshotQueryKeys.costBenefit,
};

/**
 * QL-314 / QL-313 — derived queries that must be invalidated alongside a
 * snapshot type. The volatility and equity share charts read the
 * asset_class_forecast snapshot server-side, so they must refetch whenever
 * that snapshot is regenerated. Kept as a companion map rather than adding
 * virtual SnapshotTypes, since neither is a stored snapshot row.
 */
const COMPANION_KEYS: Partial<
  Record<SnapshotType, ((goalId: string | null) => readonly unknown[])[]>
> = {
  assetClassForecast: [
    goalSnapshotQueryKeys.volatilityChart,
    goalSnapshotQueryKeys.equityShareChart,
    // QL-375 — the mandate check reads its year-1 basis from the same snapshot.
    goalSnapshotQueryKeys.mandate,
  ],
};

/**
 * QL-315 — invalidate the instance-wide asset-class forecast without knowing
 * the instance id.
 *
 * The goal helper below is handed a goalId, and the instance row's key is a
 * sibling of the per-goal subtree rather than inside it. A session only ever
 * shows one instance, so matching every instance-scoped asset-class-forecast
 * key is both sufficient and harmless — and it keeps every caller of the goal
 * helper from having to thread an instance id it does not have.
 */
function invalidateInstanceAssetClassForecast(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({
    predicate: ({ queryKey: k }) =>
      k[0] === "flowInstances" && k[2] === "snapshot" && k[3] === "instanceAssetClassForecast",
  });
}

/**
 * QL-315 — invalidate every asset-class forecast the session holds, both
 * scopes, with no id at hand.
 *
 * For the callers that change the merge without going through a portfolio
 * calculation and therefore get no `refreshHints`: adding or deleting a goal,
 * deleting a portfolio. Both scopes are regenerated server-side on those paths,
 * so both have to be refetched.
 */
export function useInvalidateAssetClassForecast() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      predicate: ({ queryKey: k }) =>
        k[0] === "flowInstances" &&
        ((k[3] === "snapshot" && k[4] === "assetClassForecast") ||
          (k[2] === "snapshot" && k[3] === "instanceAssetClassForecast")),
    });
  };
}

export function useInvalidateGoalSnapshots() {
  const queryClient = useQueryClient();
  return (goalId: string, refreshHints?: { invalidated?: SnapshotType[]; cleared?: boolean }) => {
    if (!refreshHints) return;
    if (refreshHints.cleared) {
      // allForGoal covers every key under ["flowInstances","goals",goalId,"snapshot",*]
      // including the QL-314 volatility chart (now keyed under the snapshot prefix).
      queryClient.invalidateQueries({ queryKey: goalSnapshotQueryKeys.allForGoal(goalId) });
      // A cleared portfolio changes both scopes of the QL-315 merge.
      invalidateInstanceAssetClassForecast(queryClient);
    } else if (refreshHints.invalidated?.length) {
      for (const type of refreshHints.invalidated) {
        const keyFn = SNAPSHOT_TYPE_TO_KEY[type];
        if (keyFn) {
          queryClient.invalidateQueries({ queryKey: keyFn(goalId) });
        }
        // QL-314 — derived queries that piggyback on this snapshot type.
        const companions = COMPANION_KEYS[type];
        if (companions) {
          for (const fn of companions) {
            queryClient.invalidateQueries({ queryKey: fn(goalId) });
          }
        }
        if (type === "assetClassForecast") {
          invalidateInstanceAssetClassForecast(queryClient);
        }
      }
    }
  };
}
